import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import AdminLayout from './AdminLayout';

const SettingsPage = () => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadUserData(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });
    return () => unsubscribe();
  }, []);

  const loadUserData = async (uid) => {
    try {
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        setUserData(userDoc.data());
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ')[0].charAt(0).toUpperCase();
  };

  const settingsItems = [
    {
      icon: 'fa-user-edit',
      label: 'Edit Profile',
      description: 'Update your name, email and personal info',
      onClick: () => navigate('/admin/account'),
    },
    {
      icon: 'fa-shield-alt',
      label: 'Two-Factor Authentication',
      description: 'Enable extra security for your account',
      onClick: () => navigate('/admin/two-factor'),
    },
    {
      icon: 'fa-bell',
      label: 'Notifications',
      description: 'Manage your notification preferences',
      onClick: () => {
        // Placeholder - could be implemented later
        alert('Notifications settings coming soon!');
      },
    },
    {
      icon: 'fa-lock',
      label: 'Security',
      description: 'Change password and security settings',
      onClick: () => navigate('/admin/account'),
    },
    {
      icon: 'fa-question-circle',
      label: 'Help & Support',
      description: 'Get help and contact support',
      onClick: () => {
        window.open('https://fodyfee.com/support', '_blank');
      },
    },
  ];

  if (loading) {
    return (
      <AdminLayout title="Settings">
        <div className="text-center py-5">
          <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
        </div>
      </AdminLayout>
    );
  }

  const displayName = userData?.name || user?.displayName || user?.email?.split('@')[0] || 'User';
  const email = user?.email || '';

  return (
    <AdminLayout title="Settings">
      <div className="settings-page">
        {/* Page Title */}
        <div className="row mb-4">
          <div className="col-12">
            <h4 className="font-weight-bold">Settings</h4>
          </div>
        </div>

        {/* Profile Card */}
        <div className="row mb-4">
          <div className="col-12">
            <div 
              className="card" 
              style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', cursor: 'pointer' }}
              onClick={() => navigate('/admin/account')}
            >
              <div className="card-body d-flex align-items-center p-4">
                <div
                  className="d-flex align-items-center justify-content-center mr-4"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    background: '#ff6b35',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1.4rem',
                  }}
                >
                  {getInitials(displayName)}
                </div>
                <div className="flex-grow-1">
                  <h5 className="font-weight-bold m-0">{displayName}</h5>
                  <p className="text-muted mb-0">{email}</p>
                </div>
                <i className="fas fa-chevron-right text-muted"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Settings Items */}
        <div className="row">
          <div className="col-12">
            {settingsItems.map((item, index) => (
              <div 
                key={index}
                className="card mb-3" 
                style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', cursor: 'pointer' }}
                onClick={item.onClick}
              >
                <div className="card-body d-flex align-items-center p-3">
                  <div
                    className="d-flex align-items-center justify-content-center mr-3"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      background: 'rgba(255,107,53,0.1)',
                      color: '#ff6b35',
                    }}
                  >
                    <i className={`fas ${item.icon}`}></i>
                  </div>
                  <div className="flex-grow-1">
                    <div className="font-weight-bold" style={{ fontSize: '0.95rem' }}>{item.label}</div>
                    <small className="text-muted">{item.description}</small>
                  </div>
                  <i className="fas fa-chevron-right text-muted"></i>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* App Info */}
        <div className="row mt-4">
          <div className="col-12 text-center">
            <p className="text-muted" style={{ fontSize: '0.85rem' }}>
              FodyFee POS Admin v1.0.0
            </p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SettingsPage;
